import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { SeriesSkeleton } from '../components/LoadingSkeleton';
import YouTubePlayer from '../components/YouTubePlayer';
import { normalizeMediaUrl, DEFAULT_POSTER_SVG } from '../utils/media';

const PlayIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l10-6.5z" /></svg>;
const LockIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9h14v-9a2 2 0 0 0-2-2Zm-7-2a2 2 0 1 1 4 0v2h-4Z" /></svg>;

export default function SeriesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [season, setSeason] = useState(null);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [streamUrl, setStreamUrl] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamError, setStreamError] = useState('');

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    api.getVideo(id).then((data) => {
      if (cancelled) return;
      setSeries(data);
      const firstSeason = [...new Set((data.episodes || []).map((episode) => episode.season))].sort((a, b) => a - b)[0];
      setSeason(firstSeason ?? null);
    }).catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id, user]);

  const seriesThumb = useMemo(() => normalizeMediaUrl(series?.thumbnail) || DEFAULT_POSTER_SVG, [series?.thumbnail]);
  const seasons = useMemo(() => [...new Set((series?.episodes || []).map((episode) => episode.season))].sort((a, b) => a - b), [series]);
  const visibleEpisodes = useMemo(() => (series?.episodes || []).filter((episode) => episode.season === season).sort((a, b) => a.episode - b.episode), [series, season]);

  const currentEpisodeIndex = useMemo(() => {
    if (!activeEpisode) return -1;
    return visibleEpisodes.findIndex((ep) => ep.id === activeEpisode.id);
  }, [visibleEpisodes, activeEpisode]);

  const nextEpisode = useMemo(() => {
    if (currentEpisodeIndex >= 0 && currentEpisodeIndex < visibleEpisodes.length - 1) {
      return visibleEpisodes[currentEpisodeIndex + 1];
    }
    return null;
  }, [visibleEpisodes, currentEpisodeIndex]);

  const upcomingEpisodes = useMemo(() => {
    if (currentEpisodeIndex >= 0) {
      return visibleEpisodes.slice(currentEpisodeIndex + 1);
    }
    return [];
  }, [visibleEpisodes, currentEpisodeIndex]);

  const purchase = () => navigate(`/payment/series/${id}`);
  const playEpisode = async (episode) => {
    if (!series.purchased) return purchase();
    setActiveEpisode(episode);
    setStreamUrl('');
    setStreamError('');
    setStreaming(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      const response = await api.streamEpisode(series.id, episode.id);
      setStreamUrl(response.data?.url || '');
    } catch (err) {
      setStreamError(err.message);
    } finally {
      setStreaming(false);
    }
  };

  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;

  if (loading) return <SeriesSkeleton />;
  if (error || !series) return <div className="not-found"><h2>Series not found</h2><Link to="/">Back to home</Link></div>;

  const price = Number(series.price).toLocaleString('en-TZ', { maximumFractionDigits: 0 });

  return (
    <div className="cinema-page">
      {activeEpisode ? (
        /* When playing: Back thumbnail/cover and Episode Guide are completely removed, player is at top, only Up Next is shown */
        <div className="series-watch-view">
          <header className="series-player-header">
            
            <div>
              
              <h1>{series.title}</h1>
            </div>
            <span className="secure-pill" style={{ justifySelf: 'end' }}>● SECURE ACCESS</span>
          </header>

          <section className="series-screen series-screen--top" aria-label={`Playing ${activeEpisode.title}`}>
            <div className="series-screen__frame">
              {streaming && <div className="player-message"><span className="cinema-spinner" />Securing your stream</div>}
              {streamError && <div className="player-message player-message--error">{streamError}<button onClick={() => playEpisode(activeEpisode)}>Try again</button></div>}
              {streamUrl && (
                <YouTubePlayer
                  key={streamUrl}
                  src={streamUrl}
                  poster={seriesThumb}
                  title={`${series.title} - S${activeEpisode.season}:E${activeEpisode.episode} ${activeEpisode.title}`}
                  autoPlay={true}
                  onNext={nextEpisode ? () => playEpisode(nextEpisode) : undefined}
                  onEnded={nextEpisode ? () => playEpisode(nextEpisode) : undefined}
                  upNext={nextEpisode ? {
                    title: nextEpisode.title,
                    subtitle: `S${nextEpisode.season}:E${nextEpisode.episode}${nextEpisode.duration ? ` · ${nextEpisode.duration}` : ''}`,
                    thumbnail: seriesThumb,
                    onPlay: () => playEpisode(nextEpisode),
                  } : null}
                />
              )}
            </div>
            <div className="now-playing">
              <span>NOW PLAYING</span>
              <div>
                <b>S{activeEpisode.season}:E{activeEpisode.episode}</b>
                <h2>{activeEpisode.title}</h2>
              </div>
            </div>
          </section>

          {upcomingEpisodes.length > 0 && (
            <div className="series-upnext" style={{ padding: '24px 20px 10px', background: '#090d16', borderRadius: '8px', marginBottom: '40px', marginTop: '20px' }}>
              <div className="episode-heading" style={{ marginBottom: 16 }}>
                <div>
                  <span style={{ color: '#ff4d4d' }}>UP NEXT</span>
                  <h2 style={{ fontSize: '1.4rem' }}>Upcoming in Season {season}</h2>
                </div>
              </div>
              <div className="episode-rail">
                {upcomingEpisodes.map((episode, idx) => (
                  <button
                    key={episode.id}
                    className="episode-tile"
                    onClick={() => playEpisode(episode)}
                  >
                    <div className="episode-tile__art" style={{ backgroundImage: `url("${seriesThumb}")` }}>
                      <span className="episode-index">{String(episode.episode).padStart(2, '0')}</span>
                      {idx === 0 && <span className="series-upnext-badge">UP NEXT</span>}
                      <span className="episode-action">{series.purchased ? <PlayIcon /> : <LockIcon />}</span>
                    </div>
                    <div className="episode-tile__copy">
                      <span>S{episode.season} · E{episode.episode}{episode.duration ? ` · ${episode.duration}` : ''}</span>
                      <h3>{episode.title}</h3>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* When not playing: Overview Hero with background thumbnail and Episode Guide */
        <>
          <section className="series-hero" style={{ '--series-art': `url("${seriesThumb}")` }}>
            <div className="series-hero__content">
              <Link to="/" className="cinema-back">← Browse</Link>
              <div className="series-kicker">ZIME SERIES <span /> {seasons.length || 1} SEASON{seasons.length === 1 ? '' : 'S'}</div>
              <h1>{series.title}</h1>
              <div className="series-meta">
                {series.rating && <strong>{series.rating}</strong>}
                {series.year && <span>{series.year}</span>}
                {series.genre && <span>{series.genre}</span>}
                <span>HD</span>
              </div>
              <p>{series.description}</p>
              {series.purchased ? (
                <button className="cinema-cta" onClick={() => visibleEpisodes[0] && playEpisode(visibleEpisodes[0])}><PlayIcon /> Start watching</button>
              ) : (
                <button className="cinema-cta" onClick={purchase}><LockIcon /> Unlock all episodes · TSh {price}</button>
              )}
              <small className="access-note">{series.purchased ? 'Your access is active on this account' : 'One payment unlocks the complete series'}</small>
            </div>
          </section>

          <main className="series-library">
            <div className="episode-heading">
              <div><span>EPISODE GUIDE</span><h2>{series.purchased ? 'Pick up the story' : 'What awaits'}</h2></div>
              <div className="season-tabs" aria-label="Choose season">{seasons.map((number) => <button key={number} className={season === number ? 'active' : ''} onClick={() => setSeason(number)}>Season {number}</button>)}</div>
            </div>

            <div className="episode-rail">
              {visibleEpisodes.map((episode) => (
                <button key={episode.id} className="episode-tile" onClick={() => playEpisode(episode)}>
                  <div className="episode-tile__art" style={{ backgroundImage: `url("${seriesThumb}")` }}>
                    <span className="episode-index">{String(episode.episode).padStart(2, '0')}</span>
                    <span className="episode-action">{series.purchased ? <PlayIcon /> : <LockIcon />}</span>
                  </div>
                  <div className="episode-tile__copy"><span>S{episode.season} · E{episode.episode}{episode.duration ? ` · ${episode.duration}` : ''}</span><h3>{episode.title}</h3></div>
                </button>
              ))}
            </div>
          </main>
        </>
      )}
    </div>
  );
}
